import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
} from '@mui/material';
import api from '../config/api';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import {
  documentPageCanvasSx,
  documentAuthPaperSx,
  documentAuthHeadingSx,
  documentAuthFieldSx,
  documentAuthLinkSx,
  documentAuthAlertSx,
} from '../constants/documentTheme';
import AuthPageBrandMark from '../components/AuthPageBrandMark';
import useAuthPageBranding from '../hooks/useAuthPageBranding';
import { withClientInviteQuery } from '../utils/clientInvite';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { brandToken, brandName, brandLogoUrl, buttonSx } = useAuthPageBranding();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={documentPageCanvasSx}>
      <DocumentMarketingHero
        eyebrow="Reset password"
        title="Forgot Your Password?"
        subtitle="Enter your email address and we'll send you a link to reset your password"
      />

      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 } }}>
        <Paper elevation={0} sx={documentAuthPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <AuthPageBrandMark brandName={brandName} brandLogoUrl={brandLogoUrl} />
            <Typography variant="h4" sx={documentAuthHeadingSx}>
              Reset Password
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={documentAuthAlertSx}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={documentAuthAlertSx}>
              If an account exists with this email, you will receive a password reset link shortly.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{ ...documentAuthFieldSx, mt: 0 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disableElevation
              disabled={loading || !email}
              sx={buttonSx}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to={withClientInviteQuery('/login', brandToken)}
                sx={documentAuthLinkSx}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
